import React from "react";
import { WARMUP_MESSAGES } from "./WarmupMessages";
import { IconButton } from "@/components/ds";

// Pick a stable set of 8 messages for today, seeded by the calendar date.
function getDailyMessages() {
  const seed = new Date().toDateString(); // e.g. "Sun Mar 15 2026"
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }

  // Skip loading-state messages (dots: true) — they don't read well standalone
  const pool = WARMUP_MESSAGES.filter((m) => !m.dots);
  const count = 8;
  const result = [];
  let idx = hash % pool.length;
  for (let i = 0; i < count; i++) {
    result.push(pool[idx % pool.length]);
    idx = (idx * 6364136223846793005 + 1442695040888963407) >>> 0; // LCG step
    idx = idx % pool.length;
  }
  return result;
}

interface Props {
  onClose(): void;
}

export default function TodaysTipsModal({ onClose }: Props) {
  const messages = React.useMemo(getDailyMessages, []);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("keydown", handleKey);
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [onClose]);

  return (
    <>
      <style>{`
        @keyframes v-tips-in {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .v-tips-modal {
          animation: v-tips-in 180ms ease-out both;
        }
      `}</style>
      <div
        ref={ref}
        className="v-tips-modal absolute bottom-0 left-full ml-2 z-30 w-80 rounded-xl shadow-xl border border-zinc-200 bg-white overflow-hidden"
        role="dialog"
        aria-label="Today's inspiration"
      >
        <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
          <span className="text-sm font-medium text-zinc-700">
            Today's inspiration
          </span>
          <IconButton onClick={onClose} aria-label="Close" size="sm" className="!text-zinc-400 hover:!text-zinc-600 hover:!bg-transparent">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M1 1l12 12M13 1L1 13"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </IconButton>
        </div>
        <ul className="divide-y divide-zinc-50 max-h-80 overflow-y-auto">
          {messages.map((msg, i) => (
            <li
              key={i}
              className="px-4 py-3 text-sm text-zinc-600 leading-relaxed"
            >
              {msg.text}
            </li>
          ))}
        </ul>
        <div className="px-4 py-2 bg-zinc-50 text-xs text-zinc-400 border-t border-zinc-100">
          Refreshes daily · powered by Velaro
        </div>
      </div>
    </>
  );
}
